/**
 * Pure presentation for the research snapshot — renders the executive summary,
 * verdict and evidence citations that were copied onto an Investigation when it
 * was promoted from a hypothesis finding, plus a "Regenerate evidence" action.
 *
 * Visibility:
 *  - When the investigation has snapshot data (summary / confidence /
 *    evidence) → renders the full panel + "Regenerate" button.
 *  - When the snapshot is empty but the investigation has entities or notes
 *    → renders a compact CTA card with a "Generate evidence" button.
 *  - When there's nothing to synthesise from → renders nothing (legacy
 *    hand-built investigations don't get a stray empty block).
 *
 * The connected {@link import('@/features/investigations/ResearchSnapshotPanel')}
 * wrapper supplies `busy` and `onRegenerate` (store + toast).
 *
 * Re-uses the `EvidenceCard` from the search/research detail so the citation
 * list looks identical on both sides of the workflow.
 */

import { Check, HelpCircle, Loader2, Sparkles, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EvidenceCard } from '@/components/search/detail/EvidenceCard'
import { Section } from '@/components/search/detail/Section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { Investigation, InvestigationConfidence } from '@/types/investigation'

function VerdictIcon({ verdict }: { verdict: InvestigationConfidence }) {
  if (verdict === 'supported') return <Check className="size-4 text-primary" />
  if (verdict === 'refuted') return <X className="size-4 text-destructive" />
  return <HelpCircle className="size-4 text-muted-foreground" />
}

export interface ResearchSnapshotPanelViewProps {
  inv: Investigation
  /** True while a regenerate/generate request is in flight. */
  busy: boolean
  /** Re-run the search agent against the investigation's current state. */
  onRegenerate: () => void
}

export function ResearchSnapshotPanelView({
  inv,
  busy,
  onRegenerate,
}: ResearchSnapshotPanelViewProps) {
  const { t } = useTranslation()

  const hasSummary = Boolean(inv.summary?.trim())
  const hasConfidence = Boolean(inv.confidence)
  const hasEvidence = Array.isArray(inv.evidence) && inv.evidence.length > 0
  const hasSnapshot = hasSummary || hasConfidence || hasEvidence
  // We can synthesise from scratch when the investigation has anything to
  // describe (entities or notes). If both are empty, the LLM has nothing to
  // ground citations in, so we hide the panel entirely.
  const canSynthesise = (inv.graphView.nodes?.length ?? 0) > 0 || (inv.notes?.length ?? 0) > 0

  if (!hasSnapshot && !canSynthesise) return null

  return (
    <div className="flex flex-col gap-4">
      {hasSnapshot ? (
        <>
          {(hasSummary || hasConfidence) && (
            <Section
              title={t('search.detail.summary.title')}
              hint={
                inv.sourceRunId
                  ? t('investigations.snapshot.fromResearch', 'Snapshot from research')
                  : undefined
              }
            >
              {hasSummary ? (
                <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
                  {inv.summary}
                </p>
              ) : (
                <p className="text-sm italic text-muted-foreground">
                  {t('search.detail.summary.noSummary')}
                </p>
              )}
              {hasConfidence && inv.confidence && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  <VerdictIcon verdict={inv.confidence} />
                  <span className="font-medium uppercase tracking-wider">
                    {t(`search.detail.verdictLabel.${inv.confidence}`)}
                  </span>
                  {inv.confidenceText && (
                    <span className="text-muted-foreground">— {inv.confidenceText}</span>
                  )}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRegenerate}
                  disabled={busy}
                  aria-busy={busy}
                >
                  {busy ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="size-3.5" />
                  )}
                  {t('investigations.snapshot.regenerate', 'Regenerate evidence')}
                </Button>
              </div>
            </Section>
          )}

          {hasEvidence && <EvidenceCard evidence={inv.evidence} />}
        </>
      ) : (
        <Card variant="dashed" className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-sm font-semibold tracking-tight">
                {t('investigations.snapshot.emptyTitle', 'No evidence snapshot yet')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  'investigations.snapshot.emptyHint',
                  'Run the search agent against this investigation’s entities and notes to draft a summary, verdict and citation list.'
                )}
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={onRegenerate}
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {t('investigations.snapshot.generate', 'Generate evidence')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
